import { useState, useEffect, useCallback } from 'react'
import { formatDate, formatDateTimeFull } from '@utils/dateUtils'
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Collapse,
  Autocomplete,
} from '@mui/material'
import {
  History,
  Search,
  FilterList,
  Refresh,
  Add,
  Edit,
  Delete,
  Login,
  Logout,
  Visibility,
  Download,
  ExpandMore,
  ExpandLess,
  Person,
  Computer,
  AccessTime,
  DataObject,
  Print,
  Clear,
  DateRange,
} from '@mui/icons-material'
import { PageHeader, Button, Card, EmptyState } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'
import { useAuth } from '@context/AuthContext'

const ACTION_ICONS = {
  create: <Add />,
  update: <Edit />,
  delete: <Delete />,
  login: <Login />,
  logout: <Logout />,
  view: <Visibility />,
  export: <Download />,
}

const ACTION_COLORS = {
  create: '#10b981',
  update: '#3b82f6',
  delete: '#ef4444',
  login: '#8b5cf6',
  logout: '#6b7280',
  view: '#0891b2',
  export: '#f59e0b',
}

const formatDateTimeLocal = (dateString) => {
  if (!dateString) return '-'
  return formatDateTimeFull(dateString) || '-'
}

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return formatDate(dateString)
}

const ChangesViewer = ({ changes }) => {
  const [open, setOpen] = useState(false)

  if (!changes || Object.keys(changes).length === 0) {
    return <Typography variant="caption" color="text.secondary">No changes</Typography>
  }

  return (
    <Box>
      <Button
        size="small"
        variant="text"
        onClick={() => setOpen(!open)}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        sx={{ textTransform: 'none', p: 0 }}
      >
        {Object.keys(changes).length} field{Object.keys(changes).length > 1 ? 's' : ''}
      </Button>
      <Collapse in={open}>
        <Box
          sx={{
            mt: 1,
            p: 1,
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            maxHeight: 200,
            overflow: 'auto',
          }}
        >
          {Object.entries(changes).map(([field, value]) => (
            <Box key={field} mb={0.5}>
              <Typography variant="caption" fontWeight={600} color="primary">
                {field}:
              </Typography>
              {typeof value === 'object' ? (
                <Box ml={1}>
                  {value.old !== undefined && (
                    <Typography variant="caption" display="block" sx={{ color: '#ef4444' }}>
                      - {JSON.stringify(value.old)}
                    </Typography>
                  )}
                  {value.new !== undefined && (
                    <Typography variant="caption" display="block" sx={{ color: '#10b981' }}>
                      + {JSON.stringify(value.new)}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="caption" ml={1}>
                  {JSON.stringify(value)}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}

const AuditLogDetailDialog = ({ log, open, onClose }) => {
  if (!log) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar
            sx={{
              backgroundColor: `${ACTION_COLORS[log.action]}20`,
              color: ACTION_COLORS[log.action],
              width: 32,
              height: 32,
            }}
          >
            {ACTION_ICONS[log.action] || <History />}
          </Avatar>
          Audit Log Details
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Person fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">User</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {log.user_display || 'System'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <AccessTime fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Timestamp</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {formatDateTimeLocal(log.timestamp)}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <DataObject fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Model</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {log.model_name}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Chip
                label={log.action}
                size="small"
                sx={{
                  backgroundColor: `${ACTION_COLORS[log.action]}20`,
                  color: ACTION_COLORS[log.action],
                  fontWeight: 600,
                  textTransform: 'capitalize',
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Object</Typography>
            <Typography variant="body2" fontWeight={500}>
              {log.object_repr || `ID: ${log.object_id}`}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Computer fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">IP Address</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {log.ip_address || '-'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          {log.user_agent && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">User Agent</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {log.user_agent}
              </Typography>
            </Grid>
          )}
          {log.changes && Object.keys(log.changes).length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Changes
              </Typography>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <pre style={{ margin: 0 }}>{JSON.stringify(log.changes, null, 2)}</pre>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

const AuditLogsList = () => {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [totalCount, setTotalCount] = useState(0)
  const [actionFilter, setActionFilter] = useState('')
  const [modelFilter, setModelFilter] = useState('')
  const [userFilter, setUserFilter] = useState(null)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedLog, setSelectedLog] = useState(null)
  const [summary, setSummary] = useState(null)
  const [users, setUsers] = useState([])
  const [modelNames, setModelNames] = useState([])
  const { hasPermission } = useAuth()

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true)
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
      }
      if (actionFilter) params.action = actionFilter
      if (modelFilter) params.model_name = modelFilter
      if (userFilter) params.user = userFilter.id
      if (startDate) params.start_date = startDate
      if (endDate) params.end_date = endDate
      if (searchQuery) params.search = searchQuery

      const response = await api.get('/common/audit-logs/', { params })
      setLogs(response.data.results || response.data)
      setTotalCount(response.data.count || response.data.length)
    } catch (error) {
      console.error('Error fetching audit logs:', error)
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view audit logs')
      } else {
        toast.error('Failed to load audit logs')
      }
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, actionFilter, modelFilter, userFilter, startDate, endDate, searchQuery])

  const fetchSummary = async () => {
    try {
      const response = await api.get('/common/audit-logs/summary/')
      setSummary(response.data)
    } catch (error) {
      console.error('Error fetching summary:', error)
    }
  }

  const fetchFilterOptions = async () => {
    try {
      const [usersRes, modelsRes] = await Promise.all([
        api.get('/common/audit-logs/users/'),
        api.get('/common/audit-logs/model_names/'),
      ])
      setUsers(usersRes.data)
      setModelNames(modelsRes.data)
    } catch (error) {
      console.error('Error fetching filter options:', error)
    }
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      const params = {}
      if (actionFilter) params.action = actionFilter
      if (modelFilter) params.model_name = modelFilter
      if (userFilter) params.user = userFilter.id
      if (startDate) params.start_date = startDate
      if (endDate) params.end_date = endDate
      if (searchQuery) params.search = searchQuery

      const response = await api.get('/common/audit-logs/export/', {
        params,
        responseType: 'blob',
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      toast.success('Audit logs exported successfully')
    } catch (error) {
      console.error('Error exporting audit logs:', error)
      toast.error('Failed to export audit logs')
    } finally {
      setExporting(false)
    }
  }

  const handlePrint = () => {
    window.print()
  }

  const handleClearFilters = () => {
    setActionFilter('')
    setModelFilter('')
    setUserFilter(null)
    setStartDate('')
    setEndDate('')
    setSearchQuery('')
    setPage(0)
  }

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  useEffect(() => {
    fetchSummary()
    fetchFilterOptions()
  }, [])

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    setPage(0)
    fetchLogs()
  }

  const handleRefresh = () => {
    fetchLogs()
    fetchSummary()
    toast.success('Audit logs refreshed')
  }

  const hasActiveFilters = actionFilter || modelFilter || userFilter || startDate || endDate || searchQuery

  return (
    <Box>
      <PageHeader
        title="Audit Logs"
        subtitle="Track all system activities and changes"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Settings', path: '/settings' },
          { label: 'Audit Logs' },
        ]}
        actions={
          <Box display="flex" gap={1}>
            <Button startIcon={<Refresh />} variant="outlined" onClick={handleRefresh}>
              Refresh
            </Button>
            {hasPermission('audit_logs.export') && (
              <>
                <Button
                  startIcon={<Download />}
                  variant="outlined"
                  onClick={handleExport}
                  disabled={exporting}
                >
                  {exporting ? 'Exporting...' : 'Export CSV'}
                </Button>
                <Button startIcon={<Print />} variant="outlined" onClick={handlePrint}>
                  Print
                </Button>
              </>
            )}
          </Box>
        }
      />

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="primary">
                {summary.total_logs?.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Logs
              </Typography>
            </Card>
          </Grid>
          {summary.action_counts?.slice(0, 3).map((action) => (
            <Grid item xs={6} sm={3} key={action.action}>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: `${ACTION_COLORS[action.action]}20`,
                      color: ACTION_COLORS[action.action],
                    }}
                  >
                    {ACTION_ICONS[action.action]}
                  </Avatar>
                  <Typography variant="h5" fontWeight={700}>
                    {action.count.toLocaleString()}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" textTransform="capitalize">
                  {action.action}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <Box
          component="form"
          onSubmit={handleSearchSubmit}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Action</InputLabel>
                <Select
                  value={actionFilter}
                  onChange={(e) => {
                    setActionFilter(e.target.value)
                    setPage(0)
                  }}
                  label="Action"
                >
                  <MenuItem value="">All Actions</MenuItem>
                  <MenuItem value="create">Create</MenuItem>
                  <MenuItem value="update">Update</MenuItem>
                  <MenuItem value="delete">Delete</MenuItem>
                  <MenuItem value="login">Login</MenuItem>
                  <MenuItem value="logout">Logout</MenuItem>
                  <MenuItem value="view">View</MenuItem>
                  <MenuItem value="export">Export</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Model</InputLabel>
                <Select
                  value={modelFilter}
                  onChange={(e) => {
                    setModelFilter(e.target.value)
                    setPage(0)
                  }}
                  label="Model"
                >
                  <MenuItem value="">All Models</MenuItem>
                  {modelNames.map((model) => (
                    <MenuItem key={model} value={model}>
                      {model}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                size="small"
                options={users}
                getOptionLabel={(option) => option.name || option.username}
                value={userFilter}
                onChange={(e, newValue) => {
                  setUserFilter(newValue)
                  setPage(0)
                }}
                renderInput={(params) => (
                  <TextField {...params} label="User" placeholder="Filter by user" />
                )}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value)
                  setPage(0)
                }}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value)
                  setPage(0)
                }}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" gap={1}>
                <Button type="submit" variant="contained" startIcon={<FilterList />}>
                  Filter
                </Button>
                {hasActiveFilters && (
                  <Button
                    variant="outlined"
                    startIcon={<Clear />}
                    onClick={handleClearFilters}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Card>

      {/* Logs Table */}
      <Card sx={{ p: 0 }}>
        {loading ? (
          <Box p={4} textAlign="center">
            <Typography color="text.secondary">Loading audit logs...</Typography>
          </Box>
        ) : logs.length === 0 ? (
          <EmptyState
            icon={History}
            title="No audit logs found"
            description="No logs match your current filters"
          />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Object</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Changes</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow
                      key={log.id}
                      hover
                      sx={{ '&:last-child td': { border: 0 } }}
                    >
                      <TableCell>
                        <Tooltip title={formatDateTimeLocal(log.timestamp)}>
                          <Typography variant="body2">
                            {formatTimeAgo(log.timestamp)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {log.user_display || 'System'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={ACTION_ICONS[log.action]}
                          label={log.action}
                          size="small"
                          sx={{
                            backgroundColor: `${ACTION_COLORS[log.action]}20`,
                            color: ACTION_COLORS[log.action],
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            '& .MuiChip-icon': {
                              color: 'inherit',
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{log.model_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {log.object_repr || log.object_id || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {log.ip_address || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <ChangesViewer changes={log.changes} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View details">
                          <IconButton
                            size="small"
                            onClick={() => setSelectedLog(log)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Card>

      {/* Detail Dialog */}
      <AuditLogDetailDialog
        log={selectedLog}
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </Box>
  )
}

export default AuditLogsList
